import { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Loader2 } from "lucide-react";
import { adminApi, apiError } from "@/lib/api";
import { useAdminUser, AdminShell } from "@/lib/adminAuth";
import { BookingsPanel, CalendarPanel } from "@/components/admin/BookingsPanel";
import { BookingDialog } from "@/components/admin/BookingDialog";
import { ServicesPanel } from "@/components/admin/ServicesPanel";
import { AvailabilityPanel, PricingVarsPanel, AreasPanel, TemplatesPanel } from "@/components/admin/SettingsPanels";
import { RequestsPanel, RequestDialog, MetricsBar, ClientsPanel, InvoicesPanel } from "@/components/admin/RequestsPanel";
import { VendorPanel } from "@/components/admin/VendorPanel";

const TABS = [["requests", "Service requests"], ["vendor", "Vendor requests"], ["bookings", "Bookings"], ["calendar", "Calendar"], ["clients", "Clients"], ["invoices", "Invoices"], ["pricing", "Pricing & services"], ["availability", "Availability"], ["areas", "Service areas"], ["emails", "Email templates"]];

const AuthCallback = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  useEffect(() => {
    const sessionId = new URLSearchParams(hash.slice(1)).get("session_id");
    adminApi.post("/auth/google/session", { session_id: sessionId })
      .then((r) => navigate(r.data.role === "admin" ? "/admin" : "/client-portal", { replace: true }))
      .catch((e) => {
        toast.error(apiError(e));
        navigate("/admin/login", { replace: true });
      });
  }, [hash, navigate]);
  return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [user] = useAdminUser();
  const [tab, setTab] = useState("requests");
  const [bookings, setBookings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [openReq, setOpenReq] = useState(null);
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    adminApi.get("/admin/requests").then((r) => setRequests(r.data)).catch(() => {});
    adminApi.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
    adminApi.get("/admin/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    adminApi.get("/admin/field-metrics").then((r) => setMetrics(r.data)).catch(() => {});
    adminApi.get("/admin/inspections/bookings").then((r) => setBookings(r.data)).catch(() => {});
    adminApi.get("/admin/inspections/services").then((r) => setServices(r.data)).catch(() => {});
    adminApi.get("/admin/inspections/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (user === false) return <Navigate to="/admin/login" replace />;
  if (user && user.role !== "admin") return <Navigate to="/client-portal" replace />;
  if (!user || !settings) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;

  const saveSettings = async (patch) => {
    const { id, ...rest } = settings;
    const r = await adminApi.put("/admin/inspections/settings", { ...rest, ...patch });
    setSettings(r.data);
  };
  const updated = (b) => {
    setBookings((rows) => rows.map((x) => (x.reference === b.reference ? b : x)));
    setOpen(b);
  };
  const logout = async () => {
    await adminApi.post("/auth/logout");
    navigate("/admin/login");
  };

  return (
    <AdminShell>
      <div className="mx-auto max-w-7xl px-4 py-8 lg:px-8" data-testid="admin-dashboard">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-[#0B132B]">Inspection management</h1>
            <p className="text-sm text-slate-500">Signed in as {user.email}</p>
          </div>
          <button type="button" onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
        <div className="mt-6"><MetricsBar m={metrics} /></div>
        <nav className="mt-6 flex gap-2 overflow-x-auto pb-2">
          {TABS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium ${tab === k ? "bg-[#0B132B] text-white" : "bg-white text-slate-600"}`}>{label}</button>
          ))}
        </nav>
        <div className="mt-6 space-y-6">
          {tab === "requests" && <RequestsPanel requests={requests} onOpen={setOpenReq} />}
          {tab === "vendor" && <VendorPanel />}
          {tab === "clients" && <ClientsPanel clients={clients} />}
          {tab === "invoices" && <InvoicesPanel invoices={invoices} />}
          {tab === "bookings" && <BookingsPanel bookings={bookings} onOpen={setOpen} />}
          {tab === "calendar" && <CalendarPanel bookings={bookings} onOpen={setOpen} />}
          {tab === "pricing" && <><ServicesPanel services={services} onSaved={(s) => setServices((l) => l.map((x) => (x.id === s.id ? s : x)))} /><PricingVarsPanel settings={settings} onSave={saveSettings} /></>}
          {tab === "availability" && <AvailabilityPanel settings={settings} onSave={saveSettings} />}
          {tab === "areas" && <AreasPanel settings={settings} onSave={saveSettings} />}
          {tab === "emails" && <TemplatesPanel settings={settings} onSave={saveSettings} />}
        </div>
      </div>
      {openReq && <RequestDialog key={openReq.request_number} r={openReq} onClose={() => { setOpenReq(null); load(); }}
        onUpdated={(r) => { setOpenReq(r); setRequests((l) => l.map((x) => (x.request_number === r.request_number ? r : x))); }} />}
      {open && <BookingDialog key={open.reference} booking={open} autoNotify={settings.auto_notify} onClose={() => setOpen(null)} onUpdated={updated} />}
    </AdminShell>
  );
};

export default function AdminDashboard() {
  const { hash } = useLocation();
  if (hash?.includes("session_id=")) return <AuthCallback />;
  return <Dashboard />;
}
